/**
 * Paddle Billing (web ödeme). Satın alım Adapty'ye Paddle webhook'u ile düşer;
 * `custom_data.customer_user_id` = Supabase kullanıcı kimliği (mobilin
 * Adapty.identify ile kullandığı kimlik). Böylece aynı hesap iOS, Android ve
 * web'de premium olur; `sync-premium` değişmeden çalışır.
 */
type PaddleGlobal = {
  Environment: { set: (env: 'sandbox' | 'production') => void };
  Initialize: (opts: { token: string; eventCallback?: (e: { name: string; data?: unknown }) => void }) => void;
  Checkout: {
    open: (opts: {
      items: { priceId: string; quantity: number }[];
      customer?: { email: string };
      customData?: Record<string, string>;
      settings?: {
        displayMode?: 'overlay' | 'inline';
        theme?: 'light' | 'dark';
        locale?: string;
        allowLogout?: boolean;
        frameTarget?: string;
        frameInitialHeight?: number;
        frameStyle?: string;
        successUrl?: string;
      };
    }) => void;
    close: () => void;
  };
  PricePreview: (req: { items: { priceId: string; quantity: number }[] }) => Promise<{
    data: { currencyCode: string; details: { lineItems: { price: { id: string }; formattedTotals: { total: string; subtotal: string } }[] } };
  }>;
};

declare global {
  interface Window {
    Paddle?: PaddleGlobal;
  }
}

const TOKEN = import.meta.env.VITE_PADDLE_CLIENT_TOKEN as string | undefined;
const ENV = (import.meta.env.VITE_PADDLE_ENV as 'sandbox' | 'production' | undefined) ?? 'sandbox';
export const PRICE_MONTHLY = import.meta.env.VITE_PADDLE_PRICE_MONTHLY as string | undefined;
export const PRICE_YEARLY = import.meta.env.VITE_PADDLE_PRICE_YEARLY as string | undefined;

// Güvenlik: sandbox ödemesi yalnız yerel geliştirmede açılır. Canlı sitede
// test kartıyla gerçek hesaplara premium verilmesin.
const sandboxAllowed = ENV !== 'sandbox' || ['localhost', '127.0.0.1'].includes(location.hostname);
// Canlı ödeme, Paddle hesapta checkout'u açana (alan adı onayı) kadar kapalı:
// VITE_PADDLE_LIVE_READY=1 olmadan canlı checkout hiç gösterilmez.
const liveReady = ENV !== 'production' || import.meta.env.VITE_PADDLE_LIVE_READY === '1';
export const paddleEnabled = !!(TOKEN && PRICE_MONTHLY && PRICE_YEARLY) && sandboxAllowed && liveReady;

let loading: Promise<PaddleGlobal> | null = null;
const completedListeners = new Set<() => void>();
const eventListeners = new Set<(name: string) => void>();

export function onCheckoutEvent(cb: (name: string) => void): () => void {
  eventListeners.add(cb);
  return () => eventListeners.delete(cb);
}

export function onCheckoutCompleted(cb: () => void): () => void {
  completedListeners.add(cb);
  return () => completedListeners.delete(cb);
}

/** Paddle.js'i ilk ihtiyaçta yükler (sayfa açılışını yavaşlatmasın diye). */
export function loadPaddle(): Promise<PaddleGlobal> {
  if (!TOKEN) return Promise.reject(new Error('paddle disabled'));
  loading ??= new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://cdn.paddle.com/paddle/v2/paddle.js';
    s.async = true;
    s.onload = () => {
      const P = window.Paddle;
      if (!P) return reject(new Error('paddle missing'));
      if (ENV === 'sandbox') P.Environment.set('sandbox');
      P.Initialize({
        token: TOKEN,
        eventCallback: (e) => {
          if (e.name === 'checkout.completed') completedListeners.forEach((l) => l());
          eventListeners.forEach((l) => l(e.name));
        },
      });
      resolve(P);
    };
    s.onerror = () => {
      loading = null;
      reject(new Error('paddle load failed'));
    };
    document.head.appendChild(s);
  });
  return loading;
}

export async function openCheckout(opts: { priceId: string; userId: string; email?: string | null; locale: string; dark: boolean }) {
  const P = await loadPaddle();
  P.Checkout.open({
    items: [{ priceId: opts.priceId, quantity: 1 }],
    customer: opts.email ? { email: opts.email } : undefined,
    customData: { customer_user_id: opts.userId },
    settings: { displayMode: 'overlay', theme: opts.dark ? 'dark' : 'light', locale: opts.locale, allowLogout: false },
  });
}

/** Sayfa içine gömülü ödeme formu (/checkout). */
export async function openInlineCheckout(opts: { target: string; priceId: string; userId: string; email?: string | null; locale: string; dark: boolean }) {
  const P = await loadPaddle();
  P.Checkout.open({
    items: [{ priceId: opts.priceId, quantity: 1 }],
    customer: opts.email ? { email: opts.email } : undefined,
    customData: { customer_user_id: opts.userId },
    settings: {
      displayMode: 'inline',
      frameTarget: opts.target,
      frameInitialHeight: 480,
      frameStyle: 'width: 100%; min-width: 300px; background-color: transparent; border: none;',
      theme: opts.dark ? 'dark' : 'light',
      locale: opts.locale,
      allowLogout: false,
      successUrl: `${location.origin}/checkout/success`,
    },
  });
}

export function closeCheckout() {
  window.Paddle?.Checkout.close();
}

/** Ziyaretçinin konumuna göre yerelleştirilmiş fiyatlar (vergi dahil gösterim Paddle'a ait). */
export async function previewPrices(): Promise<Record<string, string> | null> {
  if (!paddleEnabled || !PRICE_MONTHLY || !PRICE_YEARLY) return null;
  const P = await loadPaddle();
  const r = await P.PricePreview({ items: [{ priceId: PRICE_MONTHLY, quantity: 1 }, { priceId: PRICE_YEARLY, quantity: 1 }] });
  const out: Record<string, string> = {};
  for (const li of r.data.details.lineItems) out[li.price.id] = li.formattedTotals.total;
  return out;
}
